/**
 * TakeoverTitleHeader — "what this frame is" plus the way to dismiss it.
 *
 * The second row of a full-frame takeover, under `WorkflowCenterBar` and the
 * back row: a title, a line of subtext saying what the Servicer is expected to
 * do, and Close on the right. Extracted from `InstanceDetailDrawer`'s inline
 * header so the run drawer and the "pick a workflow" takeover are the same
 * chrome rather than two imitations of it.
 *
 * `rightSlot` exists because the drawer puts status pills and the oversight
 * badge beside its Close; a takeover with nothing to say there passes nothing.
 * Close is optional for the same reason — a surface whose only exit is the
 * back row above simply omits it.
 *
 * Shared — no Office.js, no Remix import.
 */
import type { ReactNode } from "react";

export interface TakeoverTitleHeaderProps {
  title: string;
  /** One line on what to do here — "Configure inputs and launch". */
  subtitle?: ReactNode;
  /** Badges/pills rendered left of Close. */
  rightSlot?: ReactNode;
  /** Omitted → no Close button (the back row is then the only exit). */
  onClose?: () => void;
  /** Appended to Close's tooltip, e.g. "(Esc)" — the shortcut is real but
   *  invisible, so the host that has it says so. */
  closeHint?: string;
}

export function TakeoverTitleHeader({
  title,
  subtitle,
  rightSlot,
  onClose,
  closeHint,
}: TakeoverTitleHeaderProps) {
  return (
    <header className="flex flex-shrink-0 items-center justify-between px-4 py-3 border-b border-primary-gray-light-200">
      <div className="flex flex-col min-w-0">
        <h2 className="text-md font-semibold text-primary-gray-light-900 truncate">
          {title}
        </h2>
        {subtitle !== undefined && (
          <p className="text-2xs text-primary-gray-light-500">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {rightSlot}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="touch-target rounded border border-primary-gray-light-300 bg-base-white px-3 py-1 text-xs font-medium text-primary-gray-light-600 hover:bg-primary-gray-light-50 transition-colors duration-150"
            aria-label="Close"
            title={closeHint ? `Close ${closeHint}` : "Close"}
          >
            Close
          </button>
        )}
      </div>
    </header>
  );
}

export default TakeoverTitleHeader;
